import Button from '@atoms/Button/Button';
import CheckboxInput from '@atoms/Inputs/CheckboxInput';
import {
  CATERING_INDUSTRY_FUNCTION_CATEGORY_CODE,
  FACILITY_FUNCTION_CATEGORY_CODE,
  HEALTHCARE_FUNCTION_CATEGORY_CODE,
  MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
  STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
  TREATMENT_FUNCTION_CATEGORY_CODE,
  VITALIS_BESTUURSCENTRUM_CLUSTER_ID,
  VITALIS_DILLENBURGSTRAAT_CLUSTER_ID,
  VITALIS_FLEXPOOL_CLUSTER_ID,
  WELL_BEING_FUNCTION_CATEGORY_CODE,
  ZZP_APPLICATION_CODE,
} from '@constants';
import { vacancyFiltersType } from '@lib/proptypes';
import MatchMakerQuestion from '@molecules/MatchMaker/MatchMakerQuestion';
import Modal from '@molecules/Modal/Modal';
import InfoCircleIcon from '@svg/info-circle.svg';
import { useMatchmakerQuestion } from '@utils';
import useApplicationFormsAnswer from '@utils/matchmaker/useApplicationFormsAnswer';
import useClusteredLocations from '@utils/matchmaker/useClusteredLocations';
import useFunctionCategoriesAnswer from '@utils/matchmaker/useFunctionCategoriesAnswer';
import PropTypes from 'prop-types';
import { useState } from 'react';

import locations from '../../../config/matchmaker/locations';
import styles from './Matchmaker.module.scss';

const MODAL_ID = 'q7-info-modal';

const filterKey = 'locations';

export default function Question7({ filters, handleNavigation }) {
  const [showHelp, setShowHelp] = useState(false);

  const { selectedApplicationFormCode } = useApplicationFormsAnswer(filters);
  const { selectedFunctionCategoryCode } = useFunctionCategoriesAnswer();

  const clusteredLocations = useClusteredLocations(filters.locations);

  const filteredClusteredLocation = clusteredLocations.filter(cluster => {
    if (!cluster.locations.length) return false;

    if (
      [
        WELL_BEING_FUNCTION_CATEGORY_CODE,
        HEALTHCARE_FUNCTION_CATEGORY_CODE,
        TREATMENT_FUNCTION_CATEGORY_CODE,
        FACILITY_FUNCTION_CATEGORY_CODE,
        CATERING_INDUSTRY_FUNCTION_CATEGORY_CODE,
      ].includes(selectedFunctionCategoryCode) &&
      [
        VITALIS_BESTUURSCENTRUM_CLUSTER_ID,
        VITALIS_DILLENBURGSTRAAT_CLUSTER_ID,
      ].includes(cluster.id)
    ) {
      return false;
    }

    if (
      [
        STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
        MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
      ].includes(selectedFunctionCategoryCode) &&
      [VITALIS_FLEXPOOL_CLUSTER_ID].includes(cluster.id)
    ) {
      return false;
    }

    if (
      selectedApplicationFormCode === ZZP_APPLICATION_CODE &&
      ![VITALIS_FLEXPOOL_CLUSTER_ID].includes(cluster.id)
    ) {
      return false;
    }

    return true;
  });

  const {
    inputs,
    handleChange,
    hasSelectedAll,
    toggleAll,
    handlePreviousUrl,
    handleNextUrl,
  } = useMatchmakerQuestion({
    key: filterKey,
    callback: handleNavigation,
    filters: { locations: filteredClusteredLocation },
    initialState: state => {
      if (!state.locations) return [];

      return filteredClusteredLocation
        .filter(cluster =>
          cluster.locations.some(location =>
            state.locations.includes(location.id.toString())
          )
        )
        .map(cluster => cluster.id.toString());
    },
    saveStateAs: () => {
      const selected = filteredClusteredLocation
        .filter(cluster => inputs.locations.includes(cluster.id.toString()))
        .map(cluster => cluster.locations)
        .flat();

      if (!selected.length) return { locations: [] };

      return {
        locations: selected.map(location => location.id.toString()),
      };
    },
  });

  const shouldSkipPreviousQuestion = () =>
    [
      STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
      MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
    ].includes(selectedFunctionCategoryCode);

  const getPreviousLink = () =>
    shouldSkipPreviousQuestion() ? '/matchmaker/5' : '/matchmaker/6';

  return (
    <MatchMakerQuestion
      number={7}
      total={7}
      question="Waar wil je werken?"
      extraClasses={styles.matchMakerQuestion}
    >
      <div className={styles.inputsContainer}>
        {filteredClusteredLocation.map(({ id, title }) => (
          <CheckboxInput
            key={`location-${id}`}
            id={id}
            value={id}
            name={filterKey}
            onChange={handleChange}
            label={title}
            checked={inputs.locations.includes(id.toString())}
            groupCheckbox
          />
        ))}

        {filteredClusteredLocation.length > 1 && (
          <CheckboxInput
            id="location-all"
            value=""
            name={filterKey}
            onChange={toggleAll}
            label="Maakt me niet uit"
            checked={hasSelectedAll}
            groupCheckbox
          />
        )}
      </div>

      {clusteredLocations.length > 1 && (
        <>
          <Button
            label="Wat betekent dit?"
            onClick={() => setShowHelp(true)}
            variant="text"
            iconLeft
            Icon={InfoCircleIcon}
            noAnimation
            modifierClass="largeIcon"
            aria-expanded={showHelp}
            aria-controls={MODAL_ID}
          />

          <Modal
            id={MODAL_ID}
            show={showHelp}
            title="Wat betekent dit?"
            onClose={() => setShowHelp(false)}
          >
            {locations.map((location, index) => (
              <>
                <h3 className="u-typography-heading-4 u-mb-1">
                  {location.title}
                </h3>
                <ul className={styles.locationList}>
                  {location.children.map(child => (
                    <li key={child}>{child}</li>
                  ))}
                </ul>
                {index !== locations.length - 1 && <br />}
              </>
            ))}
          </Modal>

          <p className={styles.choiceHelp}>Je mag meerdere opties aanvinken</p>
        </>
      )}

      <div className={styles.ctaContainer}>
        <Button
          key="matchmaker-back-button"
          label="Terug"
          href={getPreviousLink()}
          size="smallMobile"
          onClick={handlePreviousUrl}
          color="secondary"
        />
        <Button
          key="matchmaker-next-button"
          label="Toon resultaten"
          href="/matchmaker/resultaten"
          size="smallMobile"
          onClick={handleNextUrl}
          disabled={!inputs.locations.length}
        />
      </div>
    </MatchMakerQuestion>
  );
}

Question7.propTypes = {
  filters: vacancyFiltersType,
  handleNavigation: PropTypes.func,
};
